import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type OnboardingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export interface OnboardingState {
  status: OnboardingStatus;
  completedSections: {
    basicInformation: boolean;
    businessAddress: boolean;
    primaryContact: boolean;
  };
  lastUpdated?: string;
}

interface OnboardingContextType {
  onboardingState: OnboardingState;
  updateOnboardingSection: (section: keyof OnboardingState['completedSections'], isComplete: boolean) => void;
  isOnboardingComplete: boolean;
  getOnboardingProgress: () => { completed: number; total: number; percentage: number };
  forceCompleteOnboarding: () => void;
  resetOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const STORAGE_KEY = 'petso_pro_onboarding_state';

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [onboardingState, setOnboardingState] = useState<OnboardingState>(() => {
    // Load from localStorage on init
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse onboarding state:', e);
      }
    }
    return {
      status: 'NOT_STARTED',
      completedSections: {
        basicInformation: false,
        businessAddress: false,
        primaryContact: false,
      },
    };
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(onboardingState));
  }, [onboardingState]);

  // Calculate if onboarding is complete
  const isOnboardingComplete = 
    onboardingState.completedSections.basicInformation &&
    onboardingState.completedSections.businessAddress &&
    onboardingState.completedSections.primaryContact;

  // Update status based on completed sections
  useEffect(() => {
    if (isOnboardingComplete && onboardingState.status !== 'COMPLETED') {
      setOnboardingState(prev => ({
        ...prev,
        status: 'COMPLETED',
        lastUpdated: new Date().toISOString(),
      }));
    } else if (!isOnboardingComplete && onboardingState.status === 'COMPLETED') {
      setOnboardingState(prev => ({
        ...prev,
        status: 'IN_PROGRESS',
        lastUpdated: new Date().toISOString(),
      }));
    }
  }, [isOnboardingComplete, onboardingState.status]);

  const updateOnboardingSection = (
    section: keyof OnboardingState['completedSections'],
    isComplete: boolean
  ) => {
    setOnboardingState(prev => {
      const hasAnySectionComplete = Object.values({
        ...prev.completedSections,
        [section]: isComplete,
      }).some(v => v);

      return {
        ...prev,
        completedSections: {
          ...prev.completedSections,
          [section]: isComplete,
        },
        status: hasAnySectionComplete && prev.status === 'NOT_STARTED' 
          ? 'IN_PROGRESS' 
          : prev.status,
        lastUpdated: new Date().toISOString(),
      };
    });
  };

  const getOnboardingProgress = () => {
    const sections = Object.values(onboardingState.completedSections);
    const completed = sections.filter(Boolean).length;
    const total = sections.length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  };

  const forceCompleteOnboarding = () => {
    setOnboardingState({
      status: 'COMPLETED',
      completedSections: {
        basicInformation: true,
        businessAddress: true,
        primaryContact: true,
      },
      lastUpdated: new Date().toISOString(),
    });
  };

  const resetOnboarding = () => {
    setOnboardingState({
      status: 'NOT_STARTED',
      completedSections: {
        basicInformation: false,
        businessAddress: false,
        primaryContact: false,
      },
      lastUpdated: new Date().toISOString(),
    });
  };

  return (
    <OnboardingContext.Provider
      value={{
        onboardingState,
        updateOnboardingSection,
        isOnboardingComplete,
        getOnboardingProgress,
        forceCompleteOnboarding,
        resetOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}
